/**
 * ActAsLapsed — what a firm operator sees when the 15 minutes they had standing
 * in for a client run out. (PPL-03 / BOW-02, ruling 6 of 2026-09-03.)
 *
 * ⛔ WHAT USED TO HAPPEN, AND WHY IT LOOKED LIKE A BUG IN THE SCREEN.
 *
 * The act-as token lives 15 minutes (`backend/src/handlers/firm-act-as.ts`,
 * `FIRM_ACT_TTL_SECONDS`). When it lapsed, two things happened in quick
 * succession and neither of them said a word to the person at the keyboard:
 *
 *   1. the next data call reached `lib/api/client.ts`, which CLEARS the firm-act
 *      store and throws ACT_AS_EXPIRED before `fetch` (correctly: falling back
 *      to the operator's own session would read and write the FIRM's tenant);
 *   2. `routes/app/_layout.tsx` re-rendered, saw a firm-staff role on /app with
 *      no act-as, treated that as the post-"Exit to clients" anomaly it was
 *      written for, and navigated to /admin/client-book.
 *
 * From the outside that is a click that throws you out of the client. Waldo hit
 * it on People & access (PPL-03) and again walking Beneficial ownership to
 * Beneficiaries (BOW-02), and read it as the screen misbehaving. Paul, on the
 * recording: "That's interesting." Two register rows, one mechanism, and the
 * screens themselves could do nothing about it: the header of
 * `routes/app/trusts/$id/people-screen.tsx` says so in as many words.
 *
 * ── THE RULING ───────────────────────────────────────────────────────────────
 *
 * Widen the guard, keep the TTL. The 15 minutes are a security decision and are
 * not touched here. What changes is that running out of them is now an ANSWER on
 * the screen the operator is standing on, with a button that puts them back
 * where they were, instead of a silent journey to a different part of the
 * product.
 *
 * ── WHY RE-MINTING HERE IS NOT A SECURITY RELAXATION ─────────────────────────
 *
 * The button calls the same grant-gated endpoint the Client Book calls
 * (`POST /v1/firm/clients/{id}/act-as`), on the operator's own live session, via
 * `mutateReplaySafe` so the server-side step-up challenge still lands. A revoked
 * grant, a read-only grant or a signed-out operator all fail here exactly as
 * they fail there. This is "go back to the Client Book and open the client
 * again" without the journey, which is what the operator was going to do anyway.
 *
 * Deliberately NOT automatic. `use-act-as-renewal.ts` renews silently, but only
 * at the point of a WRITE the user has just asked for, so an MFA prompt lands on
 * a deliberate action. A background renewal on every idle screen would keep a
 * client's records open indefinitely on an unattended machine, which is what the
 * TTL exists to prevent.
 */

import { useCallback, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  lastKnownActAsSubject,
  useActingContext,
  useFirmActControls,
} from '../lib/acting/context';
import { useFirmClientAct } from '../lib/use-firm-client-act';
import { displayPersonName } from '../lib/format/person-name';

export function ActAsLapsed() {
  // Two sources on purpose, and the order matters. While the store still holds
  // the lapsed record the context names the client. The moment a data call
  // clears it (which is the common path: a background query almost always gets
  // there first) the context correctly reports nobody, and the sticky fact in
  // `last-act-as.ts` is the only thing left that knows who this was. That module
  // exists for exactly this and is never cleared by an expiry.
  const acting = useActingContext();
  const sticky = lastKnownActAsSubject();
  const subject = acting.posture === 'acting-as-client' ? acting.subject : sticky;
  const clientName = displayPersonName(subject?.displayName) || 'this client';
  const clientTenantId = subject?.tenantId ?? null;

  const { beginFirmAct } = useFirmActControls();
  const { mintForClient } = useFirmClientAct();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const reconnect = useCallback(async (): Promise<void> => {
    if (!clientTenantId) return;
    setBusy(true);
    setFailure(null);
    try {
      const res = await mintForClient(clientTenantId);
      beginFirmAct({
        jwt: res.token,
        clientTenantId: res.clientTenantId,
        // The label is operator-facing only and is not carried on the token, so
        // it comes from the context being restored, exactly as the renewal hook
        // does it.
        clientDisplay: subject?.displayName ?? '',
        accessLevel: res.accessLevel,
        expiresAt: res.expiresAt,
      });
      // No navigation. The layout re-renders with a live credential and the
      // screen the operator was already on comes back, which is the whole point
      // of the row: they were reading something and want to carry on reading it.
    } catch {
      // Say what a person can act on. The two real causes are a grant that has
      // been withdrawn since they opened the client, and an operator session
      // that has itself run out. Both are fixed from the client list, and
      // neither is anything they did wrong.
      setFailure(
        'We could not reconnect you to ' +
          clientName +
          '. Your access to this client may have been withdrawn, or your own sign-in may have ' +
          'run out. Open the client again from your client list, and if it is still refused, ' +
          'ask them to check your access.',
      );
    } finally {
      setBusy(false);
    }
  }, [beginFirmAct, clientName, clientTenantId, mintForClient, subject]);

  return (
    <div
      data-testid="act-as-lapsed"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--bg)',
      }}
    >
      <div
        className="card"
        role="status"
        aria-live="polite"
        style={{ padding: 28, maxWidth: 520, width: '100%' }}
      >
        <p className="eyebrow mute" style={{ margin: '0 0 6px' }}>
          Session with this client
        </p>
        <h1 style={{ margin: '0 0 14px', fontSize: 21, fontWeight: 500, color: 'var(--ink)' }}>
          Your 15 minutes acting for {clientName} are up
        </h1>
        <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.65, color: 'var(--ink-2)' }}>
          Standing in for a client lasts 15 minutes at a time, so an unattended screen never
          leaves someone else&rsquo;s records open. That window has closed.
        </p>
        <p style={{ margin: '0 0 20px', fontSize: 14, lineHeight: 1.65, color: 'var(--ink-2)' }}>
          Nothing has changed and nothing was lost. Re-enter to carry on where you were.
        </p>

        {failure !== null && (
          <div
            data-testid="act-as-lapsed-error"
            role="alert"
            style={{
              margin: '0 0 16px',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--hairline-2)',
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            {failure}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn-primary"
            data-testid="act-as-lapsed-reconnect"
            disabled={busy || clientTenantId === null}
            onClick={() => void reconnect()}
          >
            {busy ? 'Reconnecting…' : `Continue acting for ${clientName}`}
          </button>
          <Link
            to="/admin/client-book"
            data-testid="act-as-lapsed-exit"
            style={{
              fontSize: 13.5,
              color: 'var(--ink-3)',
              textDecoration: 'none',
              borderBottom: '1px solid var(--hairline-2)',
              paddingBottom: 1,
            }}
          >
            Back to your client list
          </Link>
        </div>

        {clientTenantId === null && (
          // The honest state when even the sticky fact is gone (a hard reload on
          // a lapsed tab). We cannot re-mint for a client we cannot name, and
          // guessing one would be the wrong-tenant write this whole mechanism
          // exists to prevent.
          <p style={{ margin: '16px 0 0', fontSize: 13, lineHeight: 1.55, color: 'var(--ink-3)' }}>
            We have lost track of which client this was. Open them again from your client list.
          </p>
        )}
      </div>
    </div>
  );
}
